import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { AppLayoutContext } from '../../../providers/AppLayout'
import { useUrlStateParams } from '../../../utils/url'
import { SnackbarContext } from '../../../providers/SnackbarContext'

import { AppPermissions } from '../../../models/AppPermissions.model'

import UserService from '../../../services/UserService/UserService'

import {
  Box,
  FormControl,
  OutlinedInput,
  InputLabel,
  MenuItem,
  Stack,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Paper,
  Menu,
} from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DataTable from '../../../components/DataTable/DataTable.component'
import { ID_COLUMN_WIDTH, NAME_COLUMN_MAX_WIDTH } from '../../../components/DataTable/DataTable.constants'
import Loading from '../../../components/Loading/Loading.component'
import Link from '@mui/material/Link'
import FormDialog from '../../../components/FormDialog/FormDialog.component'
import ObstructionTypeForm from '../shared/ObstructionTypeForm.component'
import { AppStateContext } from '../../../providers/AppStateContext'
import { ObstructionTypeService } from '../../../services/ObstructionTypeService/ObstructionTypeService'
import { ObstructionType } from '../../../models/ObstructionType.model'
import ConfirmDeleteDialog from '../../../components/DeleteDialog/ConfirmDeleteDialog.component'

type ObstructionTypeSearchProps = {
  nested?: boolean
  systemId?: number
  trailId?: number
  hideHeader?: boolean
  excludeColumns?: string[]
}
const ObstructionTypeSearch = (props: ObstructionTypeSearchProps): JSX.Element => {
  const { nested, excludeColumns = [] } = props

  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editObstructionType])

  const [searchError, setSearchError] = useState('')
  const [rowActionsAnchorEl, setRowActionsAnchorEl] = useState<null | HTMLElement>(null)
  const isActionsMenuOpen = Boolean(rowActionsAnchorEl)
  const [selectedRow, setSelectedRow] = useState<ObstructionType | undefined>()
  const [isAdding, setIsAdding] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)
  const currentRegionCode = AppStateContext.getRegion()

  const [name, setName] = useUrlStateParams(
    '',
    'name',
    (value) => encodeURIComponent(value.toString()),
    (value) => (value ? decodeURIComponent(value) : ''),
  )
  const nameInputRef = useRef<HTMLInputElement>()
  const [orderBy, setOrderBy] = useState<keyof ObstructionType | undefined>('sortWeight')
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc' | undefined>('asc')
  const [page, setPage] = useUrlStateParams(
    1,
    'page',
    (value) => value.toString(),
    (value) => (!isNaN(Number(value)) ? Number(value) : 1),
  )
  const [pageSize, setPageSize] = useUrlStateParams(
    12,
    'page-size',
    (value) => value.toString(),
    (value) => (!isNaN(Number(value)) ? Number(value) : 12),
  )

  useEffect(() => {
    if (!nested) {
      AppLayoutContext.setPageName('Obstruction Types')
    }
  }, [])

  const queryClient = useQueryClient()

  const obstructionTypeService = useMemo(() => new ObstructionTypeService(), []) // memo to prevent re-creating service on every render
  const { isLoading: isLoadingSponsors, data: obstructionTypesResponse } = useQuery(
    [
      'ObstructionTypeService.search',
      {
        page,
        pageSize,
        name,
        orderBy,
        orderDirection,
      },
      AppStateContext.getRegion(),
    ],
    () =>
      obstructionTypeService.search({
        page,
        pageSize,
        name,
        orderBy,
        orderDirection,
      }),
    {
      onError: (err) => {
        setSearchError(typeof err === 'string' ? err : 'An error occurred while searching for obstructionTypes.')
        console.error(err)
      },
    },
  )
  useEffect(() => {
    if (
      obstructionTypesResponse &&
      page > 1 &&
      (!obstructionTypesResponse.total || (page - 1) * pageSize >= obstructionTypesResponse.total)
    ) {
      // page out of range was requested so reset to page 1
      setPage(1)
    }
  }, [obstructionTypesResponse])

  // consolidate the various loading states into one
  useEffect(() => {
    setIsLoading(isLoadingSponsors)
  }, [isLoadingSponsors])

  const handleRowActionsClick = (event: React.MouseEvent<HTMLButtonElement>, obstructionType: ObstructionType) => {
    setRowActionsAnchorEl(event.currentTarget)
    setSelectedRow(obstructionType)
  }
  const handleRowActionsClose = () => {
    setRowActionsAnchorEl(null)
    setSelectedRow(undefined)
  }

  const columns = useMemo(() => {
    const columns: GridColDef<ObstructionType>[] = [
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        renderCell: (params) => (
          <>
            <IconButton
              id={`row-actions-button-${params.row.id}`}
              color="primary"
              aria-label="row actions"
              aria-controls={selectedRow?.id === params.row.id ? 'row-actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={selectedRow?.id === params.row.id ? 'true' : undefined}
              onClick={(event) => handleRowActionsClick(event, params.row)}
            >
              <MoreVertIcon />
            </IconButton>
          </>
        ),
      },
      {
        field: 'id',
        headerName: 'ID',
        width: ID_COLUMN_WIDTH,
        renderCell: (params) => (
          <Link color="primary" aria-label="obstruction type detail" href={`/obstruction-type/${params.row.id}`}>
            {params.row.id}
          </Link>
        ),
      },
      {
        field: 'name',
        headerName: 'Name',
        // takes the space left over by the other columns so the table fits its container instead of scrolling
        flex: 1,
        minWidth: 160,
        maxWidth: NAME_COLUMN_MAX_WIDTH,
      },
      {
        field: 'sortWeight',
        headerName: 'Sort Weight',
        width: 140,
      },
    ]
    // remove any columns specified to be removed in the excludeColumns prop
    if (excludeColumns) {
      return columns.filter((column) => !excludeColumns.includes(column.field as string))
    }
    return columns
  }, [excludeColumns])

  const handleSubmit = () => {
    setPage(1)
    setName(nameInputRef?.current?.value || '')
  }

  const handleAddClick = () => {
    setIsAdding(true)
  }

  const handleAddClose = () => {
    setIsAdding(false)
  }

  const handleEditClick = () => {
    setIsEditing(true)
  }

  const handleEditClose = () => {
    setRowActionsAnchorEl(null)
    setSelectedRow(undefined)
    setIsEditing(false)
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false)
    handleRowActionsClose()
  }

  const deleteObstructionType = useMutation(
    (mutationParams: { id: number }) => {
      setIsSaving(true)
      return obstructionTypeService.delete(mutationParams.id)
    },
    {
      onSuccess: () => {
        SnackbarContext.show('Obstruction Type deleted successfully!')
        setRowActionsAnchorEl(null)
        setSelectedRow(undefined)
        // invalidate all queries to ensure any queries that are using the modified record are updated
        queryClient.invalidateQueries()
        setIsSaving(false)
      },
      onError: (err) => {
        SnackbarContext.show(`ObstructionType failed to delete: ${err}`, 'error')
        console.error(err)
        setIsSaving(false)
      },
    },
  )

  const handleDelete = () => {
    if (selectedRow?.id) {
      setIsDeleteConfirmationOpen(false)
      deleteObstructionType.mutate({ id: selectedRow.id })
    }
  }

  return (
    <>
      {selectedRow && (
        <FormDialog<ObstructionType>
          open={isEditing}
          entity={selectedRow}
          entityLabel="Obstruction Type"
          onClose={handleEditClose}
        >
          <ObstructionTypeForm obstructionType={selectedRow} onEdit={handleEditClose} onCancel={handleEditClose} />
        </FormDialog>
      )}
      <FormDialog<ObstructionType> open={isAdding} entityLabel="Obstruction Type" onClose={handleAddClose}>
        <ObstructionTypeForm onAdd={handleAddClose} onCancel={handleAddClose} />
      </FormDialog>
      <ConfirmDeleteDialog
        open={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onDelete={handleDelete}
        title="Confirm Delete"
        content={`Are you sure you want to delete obstruction type id ${selectedRow?.id}: ${selectedRow?.name}?`}
      />
      <Paper sx={{ p: 2 }}>
        {(isLoading || isSaving) && !searchError && <Loading sx={{ py: 20 }} />}
        {searchError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{searchError}</Typography>
          </Stack>
        )}
        {!isLoading && !isSaving && !searchError && (
          <>
            <Box sx={{ pt: 1, mb: 1 }}>
              <Stack direction={{ zero: 'column', md: 'row' }} spacing={1}>
                <FormControl variant="outlined" sx={{ width: '100%' }}>
                  <InputLabel>Name</InputLabel>
                  <OutlinedInput
                    id="search-name"
                    label="Name"
                    defaultValue={name || ''}
                    inputRef={nameInputRef}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubmit()
                      }
                    }}
                    endAdornment={
                      nameInputRef?.current?.value ? (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="clear search name"
                            onClick={() => {
                              if (nameInputRef?.current?.value) {
                                nameInputRef.current.value = ''
                              }
                              setPage(1)
                              setName('')
                            }}
                            edge="end"
                          >
                            <CloseIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined
                    }
                  />
                </FormControl>

                <Button
                  variant="outlined"
                  onClick={handleSubmit}
                  sx={(theme) => ({ [theme.breakpoints.up('md')]: { width: 150 }, maxWidth: '100%' })}
                >
                  Search
                </Button>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {hasWritePermissions && (
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      color="primary"
                      aria-label="add new"
                      onClick={handleAddClick}
                    >
                      Add
                    </Button>
                  )}
                </Stack>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    if (nameInputRef?.current?.value) {
                      nameInputRef.current.value = ''
                    }
                    setPage(1)
                    setName('')
                  }}
                >
                  Clear
                </Button>
              </Stack>
            </Box>

            {obstructionTypesResponse?.data && obstructionTypesResponse.data.length > 0 ? (
              <>
                <Menu
                  id="row-actions-menu"
                  anchorEl={rowActionsAnchorEl}
                  open={isActionsMenuOpen}
                  onClose={handleRowActionsClose}
                  MenuListProps={{
                    'aria-labelledby': `row-actions-button${selectedRow?.id}`,
                  }}
                >
                  <MenuItem href={`/obstruction-type/${selectedRow?.id}`} component={Link}>
                    View
                  </MenuItem>
                  {hasWritePermissions && <MenuItem onClick={handleEditClick}>Edit</MenuItem>}
                  {hasWritePermissions && <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>}
                </Menu>
                <DataTable
                  columns={columns}
                  data={obstructionTypesResponse.data.filter((obstructionType) => obstructionType.active)}
                  total={obstructionTypesResponse.total}
                  paginationMode="server"
                  page={page}
                  pageSize={pageSize}
                  onPageChange={(newPage) => {
                    setPage(newPage)
                  }}
                  onPageSizeChange={(newPageSize) => {
                    setPageSize(newPageSize)
                  }}
                  orderBy={orderBy}
                  orderDirection={orderDirection}
                  onOrderChange={(newOrder) => {
                    setOrderBy(newOrder.field ? (newOrder.field as keyof ObstructionType) : undefined)
                    setOrderDirection(newOrder.sort)
                  }}
                />
              </>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography>No results found</Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default ObstructionTypeSearch
